import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { BookOpen, Loader2, Lock, User, Mail } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username.trim(), password);
      navigate(user.role === "admin" ? "/admin" : "/author", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const sendForgot = async () => {
    setForgotLoading(true);
    setForgotMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      setForgotMsg(data.message || "If an account exists, a reset link has been sent.");
    } catch (err) {
      setForgotMsg(apiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setForgotLoading(false);
    }
  };

  const pubName = settings?.publisher_name || "Manda Publishers";
  const logo = settings?.login_logo;
  const useGradient = settings?.login_bg_type === "gradient";
  const panelStyle = useGradient
    ? { background: settings?.login_gradient || "linear-gradient(135deg, #1f4a3a 0%, #2d6a4f 100%)" }
    : {};
  const bgImage = !useGradient ? (settings?.login_bg_image || "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200") : null;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground overflow-hidden" style={panelStyle}>
        {bgImage && (
          <div className="absolute inset-0 opacity-15 bg-cover bg-center" style={{ backgroundImage: `url('${bgImage}')` }} />
        )}
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary-foreground/15 flex items-center justify-center overflow-hidden">
            {logo ? <img src={logo} alt={pubName} className="h-full w-full object-contain" /> : <BookOpen className="h-6 w-6" />}
          </div>
          <div>
            <p className="font-head font-bold text-lg tracking-tight" data-testid="login-publisher-name">{pubName}</p>
            <p className="text-xs text-primary-foreground/70">Author Royalty Platform</p>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-head text-4xl font-bold tracking-tight leading-tight max-w-md" data-testid="login-tagline">
            {settings?.login_tagline || "Track every copy, every rupee, every royalty."}
          </h2>
          <p className="text-primary-foreground/80 mt-4 max-w-md leading-relaxed">
            A calm, transparent home for your sales reports, payouts and reprint orders — all in one place.
          </p>
        </div>
        <p className="relative text-xs text-primary-foreground/60">
          © {new Date().getFullYear()} {pubName}
        </p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <Card className="w-full max-w-md p-8 animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
              {logo ? <img src={logo} alt={pubName} className="h-full w-full object-contain" /> : <BookOpen className="h-5 w-5 text-primary-foreground" />}
            </div>
            <p className="font-head font-bold tracking-tight">{pubName}</p>
          </div>
          <h1 className="font-head text-2xl font-semibold tracking-tight" data-testid="login-title">{settings?.login_title || "Welcome back"}</h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {settings?.login_subtitle || "Sign in with the credentials issued by Manda Publishers."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">User ID</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="username"
                  data-testid="login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. admin"
                  className="pl-9 h-11"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotOpen(true); setForgotMsg(""); setForgotEmail(""); }} className="text-xs text-primary hover:underline">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 h-11"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 text-[15px]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head">Reset your password</DialogTitle>
            <DialogDescription>Enter your account email and we'll send you a reset link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@email.com" className="pl-9 h-11" />
            </div>
            {forgotMsg && <p data-testid="forgot-message" className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-md px-3 py-2">{forgotMsg}</p>}
          </div>
          <DialogFooter>
            <Button data-testid="send-reset-link" onClick={sendForgot} disabled={forgotLoading || !forgotEmail.trim()}>
              {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
